import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { 
    AvatarDirection, 
    AvatarPose, 
    getAvatarImageFallbacks,
    AVAILABLE_POSES, 
    POSE_TRANSLATIONS,
    DIRECTION_TRANSLATIONS,
    POSE_DIRECTIONS_MAP
} from '../types/avatar';
import Swal from 'sweetalert2';
import { GripVertical } from 'lucide-react';
import { getSceneAvatarDimensions } from './Meeting/mobileLayout';

interface AvatarProps {
  avatarId: string;
  username: string;
  avatarImageId: string;
  pose: AvatarPose;
  direction: AvatarDirection;
  x: number;
  y: number;
  isCurrentUser: boolean;
  canControl: boolean;
  sceneSize?: { width: number; height: number };
  /** Bumps when mobile returns to Cenário tab — forces img remount / redecode. */
  imageRemountKey?: number;
  onDrag?: (id: string, x: number, y: number) => void;
  onPoseChange?: (id: string, pose: AvatarPose) => void;
  onDirectionChange?: (id: string, direction: AvatarDirection) => void;
  onDelete?: (id: string) => void;
  isVirtual?: boolean;
}

const Avatar: React.FC<AvatarProps> = ({
  avatarId,
  username,
  avatarImageId,
  pose,
  direction,
  x,
  y,
  isCurrentUser,
  canControl,
  sceneSize = { width: 0, height: 0 },
  imageRemountKey = 0,
  onDrag,
  onPoseChange,
  onDirectionChange,
  onDelete,
  isVirtual = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStartPos, setDragStartPos] = useState<{ x: number, y: number } | null>(null);
  const [isTrueDrag, setIsTrueDrag] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const [isMenuDragging, setIsMenuDragging] = useState(false);
  const [menuDragOffset, setMenuDragOffset] = useState({ x: 0, y: 0 });
  const [lastTapPos, setLastTapPos] = useState<{ x: number, y: number } | null>(null);
  const [isControlButtonPressed, setIsControlButtonPressed] = useState(false);
  const [imageSourceIndex, setImageSourceIndex] = useState(0);
  
  const avatarRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const isMobile = useMemo(() => typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0 || window.innerWidth <= 768), []);

  const avatarDimensions = useMemo(() => {
    const dims = getSceneAvatarDimensions(sceneSize);
    return {
      width: dims.width,
      height: dims.height,
      imageHeight: dims.width,
    };
  }, [sceneSize.width, sceneSize.height]);

  const getAvatarScale = useCallback(() => {
    const smallPoses: AvatarPose[] = ['meditation', 'kneel', 'pray'];
    return smallPoses.includes(pose) ? 0.70 : 1;
  }, [pose]);

  const getEffectiveAvatarDimensions = useCallback(() => {
    const baseDimensions = { width: avatarDimensions.width, height: avatarDimensions.width };
    switch (pose) {
      case 'laydown': return { width: baseDimensions.width * 0.8, height: baseDimensions.height * 0.5 };
      case 'meditation': case 'kneel': case 'pray':
        const scale = getAvatarScale();
        return { width: baseDimensions.width * scale, height: baseDimensions.height * scale };
      default: return { width: baseDimensions.width * 0.9, height: baseDimensions.height * 0.8 };
    }
  }, [pose, avatarDimensions.width, getAvatarScale]);

  const getMenuPosition = useCallback(() => {
    const sceneContainer = document.querySelector('.scene-container');
    if (!sceneContainer) return { left: 0, top: 0 };
    
    const sceneRect = sceneContainer.getBoundingClientRect();
    const menuWidth = isMobile ? 280 : 320;
    const menuHeight = isMobile ? Math.min(window.innerHeight * 0.7, 500) : 500;
    const effectiveDimensions = getEffectiveAvatarDimensions();
    
    let menuLeft = x + effectiveDimensions.width + 10;
    let menuTop = y;
    
    if (menuLeft + menuWidth > sceneRect.width) menuLeft = Math.max(10, x - menuWidth - 10);
    if (menuTop + menuHeight > sceneRect.height) menuTop = Math.max(10, sceneRect.height - menuHeight - 10);
    
    menuTop = Math.max(10, menuTop);
    menuLeft = Math.max(10, menuLeft);
    
    return { left: menuLeft, top: menuTop };
  }, [x, y, getEffectiveAvatarDimensions, isMobile]);

  const handleInteractionStart = useCallback((clientX: number, clientY: number) => {
    if (showMenu || !canControl || isControlButtonPressed) return;
    
    // Garantir que isControlButtonPressed está limpo
    setIsControlButtonPressed(false);
    
    const rect = avatarRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({ x: clientX - rect.left, y: clientY - rect.top });
      setDragStartPos({ x: clientX, y: clientY });
      setIsDragging(true);
    }
  }, [showMenu, canControl, isControlButtonPressed]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleInteractionStart(e.clientX, e.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    handleInteractionStart(e.touches[0].clientX, e.touches[0].clientY);
  };

  const handleInteractionMove = useCallback((clientX: number, clientY: number) => {
    // CORREÇÃO: Adiciona uma verificação para ignorar o movimento do avatar se o menu estiver sendo arrastado.
    if (!isDragging || isMenuDragging || !canControl) return;

    let currentIsTrueDrag = isTrueDrag;
    if (!currentIsTrueDrag && dragStartPos) {
      const dx = Math.abs(clientX - dragStartPos.x);
      const dy = Math.abs(clientY - dragStartPos.y);
      const threshold = isMobile ? 15 : 5;
      if (dx > threshold || dy > threshold) {
        setIsTrueDrag(true);
        currentIsTrueDrag = true;
      }
    }
    if (currentIsTrueDrag) {
      const sceneContainer = document.querySelector('.scene-container');
      if (!sceneContainer) return;
      const sceneRect = sceneContainer.getBoundingClientRect();
      const effectiveDimensions = getEffectiveAvatarDimensions();
      const newX = Math.max(0, Math.min(sceneRect.width - effectiveDimensions.width, clientX - sceneRect.left - dragOffset.x));
      const newY = Math.max(0, Math.min(sceneRect.height - effectiveDimensions.height, clientY - sceneRect.top - dragOffset.y));
      onDrag?.(avatarId, newX, newY);
    }
  }, [isDragging, canControl, dragOffset, avatarId, onDrag, getEffectiveAvatarDimensions, dragStartPos, isTrueDrag, isMenuDragging, isMobile]);

  const handleMouseMove = useCallback((e: MouseEvent) => handleInteractionMove(e.clientX, e.clientY), [handleInteractionMove]);
  const handleTouchMove = useCallback((e: TouchEvent) => {
    e.preventDefault();
    handleInteractionMove(e.touches[0].clientX, e.touches[0].clientY);
  }, [handleInteractionMove]);

  const handleInteractionEnd = useCallback(() => {
    setIsDragging(false);
    setIsTrueDrag(false);
    setDragStartPos(null);
    setIsControlButtonPressed(false);
  }, []);

  const handleMouseUp = handleInteractionEnd;
  const handleTouchEnd = handleInteractionEnd;

  const handleMenuDragStart = useCallback((clientX: number, clientY: number, e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (!menuRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    setMenuDragOffset({ x: clientX - menuRect.left, y: clientY - menuRect.top });
    setIsMenuDragging(true);
  }, []);

  const handleMenuMouseDown = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    handleMenuDragStart(e.clientX, e.clientY, e);
  };

  const handleMenuTouchStart = (e: React.TouchEvent) => {
    e.preventDefault(); e.stopPropagation();
    handleMenuDragStart(e.touches[0].clientX, e.touches[0].clientY, e);
  };

  const handleMenuMove = useCallback((clientX: number, clientY: number) => {
    if (!isMenuDragging) return;
    const sceneContainer = document.querySelector('.scene-container');
    if (!sceneContainer) return;
    const sceneRect = sceneContainer.getBoundingClientRect();
    const menuWidth = isMobile ? 280 : 320;
    const menuHeight = isMobile ? Math.min(window.innerHeight * 0.7, 500) : 500;
    let newLeft = clientX - sceneRect.left - menuDragOffset.x;
    let newTop = clientY - sceneRect.top - menuDragOffset.y;
    newLeft = Math.max(10, Math.min(sceneRect.width - menuWidth - 10, newLeft));
    newTop = Math.max(10, Math.min(sceneRect.height - menuHeight - 10, newTop));
    setMenuPosition({ left: newLeft, top: newTop });
  }, [isMenuDragging, menuDragOffset, isMobile]);

  const handleMenuMouseMove = useCallback((e: MouseEvent) => handleMenuMove(e.clientX, e.clientY), [handleMenuMove]);
  const handleMenuTouchMove = useCallback((e: TouchEvent) => { e.preventDefault(); handleMenuMove(e.touches[0].clientX, e.touches[0].clientY); }, [handleMenuMove]);
  const handleMenuDragEnd = useCallback(() => setIsMenuDragging(false), []);

  useEffect(() => {
    if (isDragging && canControl) {
      // Registrar ambos os tipos de evento para suportar dispositivos híbridos (touch + mouse)
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, canControl, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  useEffect(() => {
    if (isMenuDragging) {
      // Registrar ambos os tipos de evento para suportar dispositivos híbridos (touch + mouse)
      document.addEventListener('mousemove', handleMenuMouseMove);
      document.addEventListener('mouseup', handleMenuDragEnd);
      document.addEventListener('touchmove', handleMenuTouchMove, { passive: false });
      document.addEventListener('touchend', handleMenuDragEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMenuMouseMove);
        document.removeEventListener('mouseup', handleMenuDragEnd);
        document.removeEventListener('touchmove', handleMenuTouchMove);
        document.removeEventListener('touchend', handleMenuDragEnd);
      };
    }
  }, [isMenuDragging, handleMenuMouseMove, handleMenuDragEnd, handleMenuTouchMove]);

  const handleDoubleClick = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (canControl) {
      setShowMenu(true);
      setMenuPosition(getMenuPosition());
      // Limpar estados de drag ao abrir menu
      setIsDragging(false);
      setIsControlButtonPressed(false);
    }
  }, [canControl, getMenuPosition]);

  const [lastTap, setLastTap] = useState(0);
  const handleTouchEndForMenu = useCallback((e: React.TouchEvent) => {
    if (isTrueDrag) return;
    if (!canControl) return;
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    const currentTapX = e.changedTouches[0].clientX;
    const currentTapY = e.changedTouches[0].clientY;
    
    if (tapLength < 300 && tapLength > 0 && lastTapPos) {
      const distance = Math.sqrt(
        Math.pow(currentTapX - lastTapPos.x, 2) + 
        Math.pow(currentTapY - lastTapPos.y, 2)
      );
      if (distance < 20) {
        handleDoubleClick(e);
      }
    }
    setLastTap(currentTime);
    setLastTapPos({ x: currentTapX, y: currentTapY });
  }, [lastTap, lastTapPos, handleDoubleClick, canControl, isTrueDrag]);

  const handleDirectionChange = useCallback((newDirection: AvatarDirection) => {
    onDirectionChange?.(avatarId, newDirection);
  }, [avatarId, onDirectionChange]);

  const handlePoseSelect = useCallback((newPose: AvatarPose) => {
    onPoseChange?.(avatarId, newPose);
    setShowMenu(false);
    const allowedDirections = POSE_DIRECTIONS_MAP[newPose];
    if (!allowedDirections.includes(direction)) {
      handleDirectionChange(allowedDirections[0]);
    }
  }, [avatarId, onPoseChange, direction, handleDirectionChange]);

  const rotateClockwise = useCallback(() => {
    const allowedDirections = POSE_DIRECTIONS_MAP[pose];
    const currentIndex = allowedDirections.indexOf(direction);
    const nextIndex = (currentIndex + 1) % allowedDirections.length;
    handleDirectionChange(allowedDirections[nextIndex]);
  }, [direction, pose, handleDirectionChange]);

  const rotateCounterClockwise = useCallback(() => {
    const allowedDirections = POSE_DIRECTIONS_MAP[pose];
    const currentIndex = allowedDirections.indexOf(direction);
    const nextIndex = (currentIndex - 1 + allowedDirections.length) % allowedDirections.length;
    handleDirectionChange(allowedDirections[nextIndex]);
  }, [direction, pose, handleDirectionChange]);

  const handleOutsideClick = useCallback((e: MouseEvent | TouchEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      setShowMenu(false);
    }
  }, []);

  useEffect(() => {
    if (showMenu) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.removeEventListener('touchstart', handleOutsideClick);
      };
    }
  }, [showMenu, handleOutsideClick]);

  // Prevenir menu de contexto via addEventListener para mobile
  useEffect(() => {
    const avatarElement = avatarRef.current;
    if (!avatarElement) return;

    const preventContextMenu = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Handler para touchstart não-passivo que previne o menu de contexto
    const handleTouchStartNonPassive = (e: TouchEvent) => {
      if (!canControl) return;
      // Prevenir o menu de contexto no iOS/Safari
      e.preventDefault();
    };

    // Adicionar no capture phase e bubble phase para garantir
    avatarElement.addEventListener('contextmenu', preventContextMenu, { passive: false, capture: true });
    avatarElement.addEventListener('contextmenu', preventContextMenu, { passive: false });
    
    // Adicionar listener não-passivo para touchstart
    avatarElement.addEventListener('touchstart', handleTouchStartNonPassive, { passive: false });
    
    // Também prevenir no long press (iOS específico)
    const img = avatarElement.querySelector('img');
    if (img) {
      img.addEventListener('contextmenu', preventContextMenu, { passive: false });
    }
    
    return () => {
      avatarElement.removeEventListener('contextmenu', preventContextMenu, { capture: true });
      avatarElement.removeEventListener('contextmenu', preventContextMenu);
      avatarElement.removeEventListener('touchstart', handleTouchStartNonPassive);
      if (img) {
        img.removeEventListener('contextmenu', preventContextMenu);
      }
    };
  }, [canControl, imageRemountKey, imageSourceIndex]);

  const imageSources = useMemo(
    () => getAvatarImageFallbacks(avatarImageId, pose, direction),
    [avatarImageId, pose, direction],
  );

  useEffect(() => {
    setImageSourceIndex(0);
  }, [imageSources, imageRemountKey]);

  const displaySrc = imageSources[Math.min(imageSourceIndex, imageSources.length - 1)] || "";
  const imageBroken = imageSourceIndex >= imageSources.length || !displaySrc;
  const imgKey = `${displaySrc}::${imageRemountKey}::${imageSourceIndex}`;

  const handleImageError = useCallback(() => {
    setImageSourceIndex((prev) => prev + 1);
  }, []);

  const getCursorStyle = () => canControl ? (isDragging ? 'grabbing' : 'grab') : 'default';
  const currentScale = getAvatarScale();

  const handleDelete = useCallback(() => {
    if (!onDelete || !isVirtual) return;
    // use o sweet alert2 para confirmar a deleção
    Swal.fire({
      title: 'Remover Avatar',
      text: 'Tem certeza que deseja remover este avatar do cenário?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        onDelete(avatarId);
        setShowMenu(false);
      }
    });
  }, [onDelete, isVirtual, avatarId]);

  const handleContextMenu = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  return (
    <>
      <div
        ref={avatarRef}
        className={`avatar-wrapper ${isDragging ? 'dragging' : ''} ${isCurrentUser ? 'current-user' : 'other-user'}`}
        style={{
          position: 'absolute', left: x, top: y,
          width: avatarDimensions.width, height: avatarDimensions.height,
          cursor: getCursorStyle(), zIndex: isDragging ? 999 : (isCurrentUser ? 10 : 5),
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          KhtmlUserSelect: 'none',
          pointerEvents: showMenu && canControl ? 'none' : 'auto', 
          opacity: canControl ? 1 : 0.8,
          transform: `scale(${currentScale})`, transformOrigin: 'center bottom',
          transition: isDragging ? 'none' : 'transform 0.3s ease',
        } as React.CSSProperties}
        onMouseDown={canControl ? handleMouseDown : undefined}
        onTouchStart={canControl ? handleTouchStart : undefined}
        onDoubleClick={canControl ? handleDoubleClick : undefined}
        onTouchEnd={canControl ? handleTouchEndForMenu : undefined}
        onContextMenu={handleContextMenu}
      >
        {imageBroken ? (
          <div
            className="avatar-image avatar-image-fallback"
            aria-label={`Avatar ${username}`}
            style={{
              width: '100%',
              height: avatarDimensions.imageHeight,
              borderRadius: isMobile ? '6px' : '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(148, 163, 184, 0.85)',
              color: '#1e293b',
              fontSize: isMobile ? '18px' : '22px',
              fontWeight: 700,
              pointerEvents: 'none',
            }}
          >
            ?
          </div>
        ) : (
          <img
            key={imgKey}
            src={displaySrc}
            alt={`Avatar ${username}`}
            className="avatar-image"
            draggable={false}
            loading="eager"
            decoding="async"
            onError={handleImageError}
            onContextMenu={handleContextMenu}
            style={{
              width: '100%', height: avatarDimensions.imageHeight,
              objectFit: 'cover', borderRadius: isMobile ? '6px' : '8px',
              display: 'block',
              touchAction: 'none',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              userSelect: 'none',
              pointerEvents: 'none',
            } as React.CSSProperties}
          />
        )}
        <div 
          className="avatar-name"
          style={{
            position: 'absolute', bottom: '10px', left: '50%',
            transform: 'translateX(-50%)',
            background: isCurrentUser && canControl ? 'rgba(59, 130, 246, 0.9)' : canControl ? 'rgba(34, 197, 94, 0.9)' : 'rgba(107, 114, 128, 0.9)',
            color: 'white', padding: isMobile ? '2px 6px' : '3px 8px',
            borderRadius: '4px', fontSize: isMobile ? '10px' : '12px',
            fontWeight: '500', whiteSpace: 'nowrap',
            maxWidth: `${avatarDimensions.width - 20}px`,
            overflow: 'hidden', textOverflow: 'ellipsis', zIndex: 10,
            pointerEvents: 'none',
          }}
        >
          {username}
          {canControl && (
            <span style={{ marginLeft: '4px', fontSize: '8px' }}>
              {isCurrentUser ? '🔵' : '🟢'}
            </span>
          )}
        </div>
      </div>

      {showMenu && canControl && (
        <div
          ref={menuRef}
          className="avatar-menu"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', left: menuPosition.left, top: menuPosition.top,
            zIndex: 500, background: 'white', border: '1px solid #e5e7eb',
            borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            width: isMobile ? '280px' : '320px', maxHeight: isMobile ? '70vh' : '500px',
            userSelect: 'none', display: 'flex', flexDirection: 'column'
          }}
        >
          <div
            className="menu-header"
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px', borderBottom: '1px solid #f3f4f6',
              cursor: isMenuDragging ? 'grabbing' : 'grab',
              background: isMenuDragging ? '#f9fafb' : 'transparent',
              borderRadius: '8px 8px 0 0', transition: 'background-color 0.2s ease',
              flexShrink: 0,
              gap: '12px'
            }}
            onMouseDown={handleMenuMouseDown}
            onTouchStart={handleMenuTouchStart}
          >
            {isMobile && (
              <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                <GripVertical size={20} color="#9ca3af" />
              </div>
            )}
            <h3 style={{ 
              margin: 0, 
              fontSize: isMobile ? '14px' : '16px', 
              fontWeight: '600', 
              color: '#374151', 
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              Configurações do Avatar
            </h3>
            {isVirtual && onDelete && (
              <button
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleDelete();
                }}
                style={{
                  background: '#fef2f2',
                  color: '#b91c1c',
                  border: '1px solid #fecaca',
                  borderRadius: '5px',
                  padding: isMobile ? '10px 14px' : '6px 12px',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  minHeight: isMobile ? '44px' : 'auto'
                }}
              >
                Remover
              </button>
            )}
            <button 
              onClick={() => setShowMenu(false)} 
              style={{ 
                background: 'none', 
                border: 'none', 
                fontSize: isMobile ? '24px' : '20px', 
                cursor: 'pointer', 
                color: '#9ca3af', 
                padding: isMobile ? '10px 12px' : '4px',
                minWidth: isMobile ? '44px' : 'auto',
                minHeight: isMobile ? '44px' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              ×
            </button>
          </div>
          
          <div style={{ padding: '16px', overflowY: 'auto', flexGrow: 1, maxHeight: isMobile ? 'calc(70vh - 60px)' : '440px' }}>
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>
                Poses
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '12px' }}>
                {AVAILABLE_POSES.map((p) => (
                  <button key={p} onClick={() => handlePoseSelect(p as AvatarPose)}
                    style={{
                      background: pose === p ? '#3b82f6' : '#f9fafb',
                      color: pose === p ? 'white' : '#374151',
                      border: `1px solid ${pose === p ? '#3b82f6' : '#e5e7eb'}`,
                      borderRadius: '8px', padding: isMobile ? '12px 16px' : '10px 14px',
                      cursor: 'pointer', fontSize: isMobile ? '14px' : '12px',
                      fontWeight: '500', transition: 'all 0.2s ease'
                    }}
                  >
                    {POSE_TRANSLATIONS[p as AvatarPose]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>
                Rotação
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <button onClick={rotateCounterClockwise} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: isMobile ? '14px 18px' : '12px 16px', cursor: 'pointer', fontSize: '18px' }}>
                  ⟲
                </button>
                <span style={{ fontSize: isMobile ? '14px' : '12px', color: '#6b7280', fontWeight: '500', textAlign: 'center', flex: 1 }}>
                  {DIRECTION_TRANSLATIONS[direction]}
                </span>
                <button onClick={rotateClockwise} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: isMobile ? '14px 18px' : '12px 16px', cursor: 'pointer', fontSize: '18px' }}>
                  ⟳
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Avatar;

